import $ from 'jquery';
import wretch from 'wretch';

/**
 * @desc Manages partner logos.
 * By default server returns GB version of partners list (initially hidden with CSS),
 * then we check user location if the location is detected as GB - just switch logos to be visible,
 * otherwise – make a request to the server to get a new template with local partners.
 * To prevent country_info endpoint from being overloaded with requests we save
 * location in localStorage and treat it as a valid source of truth for 24 hours since the record.
 */
// TODO: Move magic css classes to a config object
export default class PartnersSection {
  /**
   * @param {string} partnersListSelector
   * @param {string} partnerLogoSelector
   * @param {string} defaultCountryCode
   * @param {string} persistKey
   * @param {string} partnersSectionSelector
   */
  constructor({
      partnersListSelector,
      partnerLogoSelector,
      defaultCountryCode,
      persistKey,
      partnersSectionSelector,
  } = {}) {
    this.$partnersList = $(partnersListSelector);
    this.$partnersSection = $(partnersSectionSelector);

    this.partnerLogoSelector = partnerLogoSelector;
    this.partnersListSelector = partnersListSelector;
    this.defaultCountryCode = defaultCountryCode;
    this.persistKey = persistKey;

    this.init();
  }

  /**
   * @desc Checks whether a provided date is exceeded the limit or not.
   * @param {string} ISODate
   * @returns {boolean}
   */
  static isDateValid(ISODate) {
    const SHELF_LIFE = 24 * 60 * 60 * 1000; // Represents 24 Hours In Milliseconds

    return Date.now() - (new Date(ISODate)).getTime() < SHELF_LIFE;
  }

  /**
   * @returns {Promise<string|null>}
   */
  static async fetchCountryCode() {
    try {
      const { countryCode } = await wretch('https://www.mancity.com/api/cf/country_info')
        .get()
        .json();

      return countryCode;
    } catch (e) {
      console.error(`An error occurred while fetching user location: ${e}`);

      return null;
    }
  }

  async init() {
    this.isInitialized = true;

    if (
      !this.$partnersList.length
      || !this.defaultCountryCode
      || !this.persistKey
    ) {
      this.isInitialized = false;

      return;
    }

    const countryCode = await this.getCountryCode();

    if ('IntersectionObserver' in window) {
        const footerObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                   this.appendPartners(countryCode);
                   footerObserver.unobserve($('.partners-section')[0]);
                }
            });
        }, { rootMargin: '-250px 0px 0px 0px' });
        footerObserver.observe($('.partners-section')[0]);
    } else {
        this.appendPartners(countryCode);
    }
  }

  /**
   * @returns {Promise<string>}
   */
  async getCountryCode() {
    try {
      const storedLocation = this.getStoredLocation();

      if (storedLocation && PartnersSection.isDateValid(storedLocation.timestamp)) {
        return storedLocation.countryCode;
      }

      const fetchedCountryCode = await PartnersSection.fetchCountryCode();

      if (fetchedCountryCode) {
        this.storeCountryCode(fetchedCountryCode);

        return fetchedCountryCode;
      }

      return this.defaultCountryCode;
    } catch (e) {
      return this.defaultCountryCode;
    }
  }

  /**
   * @param {string} countryCode
   */
  storeCountryCode(countryCode) {
    window.localStorage.setItem(this.persistKey, JSON.stringify({
      countryCode,
      timestamp: (new Date()).toISOString(),
    }));
  }

  /**
   * @returns {{countryCode: string, timestamp: string}|null}
   */
  getStoredLocation() {
    try {
      const {
        countryCode,
        timestamp,
      } = JSON.parse(window.localStorage.getItem(this.persistKey));

      if (
        typeof countryCode !== 'string'
        || countryCode.length < 2
        || typeof timestamp !== 'string'
      ) {
        return null;
      }

      return {
        countryCode,
        timestamp,
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * @param {string} countryCode
   */
  async appendPartners(countryCode) {
      const partnersTemplate = (await this.fetchPartnersTemplate(countryCode));
      this.$partnersSection.fadeOut('fast', () => {
        this.$partnersList.replaceWith($($.parseHTML(partnersTemplate)));
        this.$partnersSection.fadeIn('fast');
      });
  }

  /**
   *
   * @param {string} countryCode
   * @param {string} baseUrl
   * @returns {Promise<string|null>}
   */
  async fetchPartnersTemplate(
    countryCode = this.defaultCountryCode,
    baseUrl = window.location.origin,
    culture = window.currentCulture,
  ) {
      try {
      let url;

      if (culture === undefined) {
          url = `${baseUrl}/web3/api/partner/logos?countryCode=${countryCode}`;
      } else {
          url = `${baseUrl}/web3/api/${culture}/partner/logos?countryCode=${countryCode}`;
      }

      return await wretch(url)
        .get()
        .text();
    } catch (e) {
      console.error(`An error occurred while fetching partners template: ${e}`);

      return null;
    }
  }
}
